<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isReplyRestricted: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['toggleMode', 'selectMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');
const wootEditorMabyMode = useTemplateRef('wootEditorMabyMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);
const mabyModeSize = useElementSize(wootEditorMabyMode);

const isPrivate = computed(() => {
  if (props.isReplyRestricted) return true;
  return props.mode === REPLY_EDITOR_MODES.NOTE;
});

const isMaby = computed(() => props.mode === REPLY_EDITOR_MODES.MABY);

const width = computed(() => {
  let widthToUse = replyModeSize.width.value;
  if (isPrivate.value) widthToUse = privateModeSize.width.value;
  if (isMaby.value) widthToUse = mabyModeSize.width.value;
  return `${widthToUse + 16}px`;
});

const translateValue = computed(() => {
  if (isPrivate.value) {
    return `${replyModeSize.width.value + 16}px`;
  }
  if (isMaby.value) {
    return `${replyModeSize.width.value + 16 + privateModeSize.width.value + 16}px`;
  }
  return '0px';
});
</script>

<template>
  <button
    class="flex items-center w-auto h-8 p-1 transition-all border rounded-full bg-n-alpha-2 group relative duration-300 ease-in-out z-0 active:scale-[0.995] active:duration-75"
    :disabled="disabled || isReplyRestricted"
    :class="{
      'cursor-not-allowed': disabled || isReplyRestricted,
    }"
    @click="$emit('toggleMode')"
  >
    <div
      ref="wootEditorReplyMode"
      class="flex items-center gap-1 px-2 z-20"
      @click.stop="$emit('selectMode', REPLY_EDITOR_MODES.REPLY)"
    >
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </div>
    <div
      ref="wootEditorPrivateMode"
      class="flex items-center gap-1 px-2 z-20"
      @click.stop="$emit('selectMode', REPLY_EDITOR_MODES.NOTE)"
    >
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </div>
    <div
      ref="wootEditorMabyMode"
      class="flex items-center gap-1 px-2 z-20"
      @click.stop="$emit('selectMode', REPLY_EDITOR_MODES.MABY)"
    >
      {{ $t('CONVERSATION.REPLYBOX.MABY') }}
    </div>
    <div
      class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-solid-1"
      :class="{
        'transition-all duration-300': !disabled && !isReplyRestricted,
      }"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </button>
</template>
